import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import ToonNPC, { NPC_PALETTE } from "@/components/scene/ToonNPC";

function makeSprite(kind) {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  const g = c.getContext("2d");
  if (kind === "paper") {
    g.fillStyle = "rgba(240,238,230,0.95)";
    g.fillRect(8, 4, 16, 24);
  } else if (kind === "frag") {
    g.fillStyle = "rgba(90,92,96,0.95)";
    g.beginPath(); g.moveTo(4, 20); g.lineTo(20, 6); g.lineTo(28, 24); g.closePath(); g.fill();
  } else {
    const grd = g.createRadialGradient(16, 16, 0, 16, 16, 16);
    grd.addColorStop(0, "rgba(200,196,186,0.7)");
    grd.addColorStop(1, "rgba(200,196,186,0)");
    g.fillStyle = grd; g.fillRect(0, 0, 32, 32);
  }
  const t = new THREE.CanvasTexture(c);
  return t;
}

function ParticleField({ kind, count, size, area, fall, getDensity }) {
  const pts = useRef();
  const tex = useMemo(() => makeSprite(kind), [kind]);
  const data = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count * 2);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * area[0];
      pos[i * 3 + 1] = Math.random() * area[1];
      pos[i * 3 + 2] = -4 - Math.random() * area[2];
      vel[i * 2] = (Math.random() - 0.5) * 0.6;
      vel[i * 2 + 1] = fall * (0.4 + Math.random() * 0.9);
    }
    return { pos, vel };
  }, [count, area, fall]);
  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(data.pos.slice(), 3));
    return g;
  }, [data]);
  const mat = useMemo(
    () => new THREE.PointsMaterial({ size, map: tex, transparent: true, depthWrite: false, opacity: 0.9 }),
    [size, tex]
  );
  useFrame((state, dt) => {
    if (!pts.current) return;
    const density = getDensity ? getDensity() : 1;
    mat.opacity = 0.35 + density * 0.55;
    const a = pts.current.geometry.attributes.position.array;
    const spd = 0.6 + density * 1.4;
    for (let i = 0; i < count; i++) {
      a[i * 3] += data.vel[i * 2] * dt * (kind === "dust" ? 0.4 : 1) * spd;
      a[i * 3 + 1] -= data.vel[i * 2 + 1] * dt * spd;
      if (a[i * 3 + 1] < 0) { a[i * 3 + 1] = area[1]; a[i * 3] = (Math.random() - 0.5) * area[0]; }
    }
    pts.current.geometry.attributes.position.needsUpdate = true;
  });
  return <points ref={pts} geometry={geom} material={mat} />;
}

// Stylized low-poly falling silhouettes — a FEW, kept far and high as distant
// "sky particles" honouring the tragedy at the top of the tower (museum distance).
function FallingSilhouettes({ getDensity }) {
  const refs = useRef([]);
  const figs = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      arr.push({ x: -14 + Math.random() * 28, z: -30 - Math.random() * 12, y: 34 + Math.random() * 44, spd: 5 + Math.random() * 3, rot: Math.random() * 6 });
    }
    return arr;
  }, []);
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ color: 0x101216 }), []);
  useFrame((state, dt) => {
    const d = getDensity ? getDensity() : 0;
    figs.forEach((f, i) => {
      const g = refs.current[i];
      if (!g) return;
      g.visible = d > 0.15;
      g.position.set(f.x, f.y, f.z);
      g.rotation.z = f.rot + state.clock.elapsedTime * 0.6;
      f.y -= f.spd * dt;
      if (f.y < 0) f.y = 34 + Math.random() * 44;
    });
  });
  return (
    <group>
      {figs.map((f, i) => (
        <mesh key={i} material={mat} ref={(el) => (refs.current[i] = el)}>
          <capsuleGeometry args={[0.55, 1.5, 4, 6]} />
        </mesh>
      ))}
    </group>
  );
}

export default function AftermathFX({ getDensity }) {
  return (
    <group>
      {/* MASSIVELY expanded aftermath — paper, dust and concrete fragments fill the air */}
      <ParticleField kind="paper" count={340} size={0.5} area={[48, 20, 26]} fall={0.7} getDensity={getDensity} />
      <ParticleField kind="paper" count={160} size={0.85} area={[52, 22, 30]} fall={0.5} getDensity={getDensity} />
      <ParticleField kind="dust" count={240} size={3.2} area={[54, 20, 30]} fall={0.22} getDensity={getDensity} />
      <ParticleField kind="frag" count={150} size={0.8} area={[44, 22, 26]} fall={1.5} getDensity={getDensity} />
      <FallingSilhouettes getDensity={getDensity} />
    </group>
  );
}
